import { PROMOTION_TYPE, type RnbCalculatorPayload } from 'typings'
import { mapPromotionBase } from '..'
import {
  MoreForLessBenefitVariant,
  MoreForLessConditions,
  MoreForLessEffect,
  MoreForLessPromotion,
  MoreForLessRestrictions,
} from '../../../../typings'
import {
  mapBin,
  mapClusterExpressions,
  mapListPrice,
  mapOrderValue,
  mapPaymentMethods,
  mapUTM,
} from '../conditions'
import {
  mapAccumulate,
  mapAccumulateWithManualPrices,
  mapBudget,
  mapMaxItemsPerCart,
  mapMaxUsagePerClient,
  mapSalesChannels,
  mapSellerIds,
  mapUsage,
} from '../restrictions'

export function mapConditions(
  data: RnbCalculatorPayload
): MoreForLessConditions {
  return {
    paymentMethods: mapPaymentMethods(data),
    orderValue: mapOrderValue(data),
    utm: mapUTM(data),
    cluster: mapClusterExpressions(data),
    bin: mapBin(data),
    listPrice: mapListPrice(data),
  }
}

export function mapMoreForLessEffect(
  data: RnbCalculatorPayload
): MoreForLessEffect {
  const isPercentualDiscount =
    data.percentualDiscountValue !== undefined &&
    data.percentualDiscountValue > 0

  return {
    trigger: {
      skus: data.listSku1BuyTogether,
      collections: data.collections1BuyTogether,
      minQuantity: data.minimumQuantityBuyTogether,
    },
    benefit: {
      quantity: data.quantityToAffectBuyTogether,
      discount: isPercentualDiscount
        ? {
            type: MoreForLessBenefitVariant.PERCENTUAL,
            value: data.percentualDiscountValue,
          }
        : {
            type: MoreForLessBenefitVariant.MAX_PRICE_PER_ITEM,
            value: data.maximumUnitPriceDiscount,
          },
      apply: {
        equals: data.enableBuyTogetherPerSku,
        mostExpensive: data.isAppliedToMostExpensive ?? false,
        distributed: data.shouldDistributeDiscountAmongMatchedItems,
      },
    },
  }
}

export function mapMoreForLessRestrictions(
  data: RnbCalculatorPayload
): MoreForLessRestrictions {
  return {
    maxUsage: mapUsage(data),
    maxUsagePerClient: mapMaxUsagePerClient(data),
    maxItemsPerCart: mapMaxItemsPerCart({
      ...data,
      isAppliedToMostExpensive: undefined,
    }),
    accumulate: mapAccumulate(data),
    accumulateWithManualPrices: mapAccumulateWithManualPrices(data),
    budget: mapBudget(data),
    sellers: mapSellerIds(data),
    salesChannels: mapSalesChannels(data),
    origin: data.origin,
  }
}

export function mapMoreForLess(
  data: RnbCalculatorPayload
): MoreForLessPromotion {
  return {
    ...mapPromotionBase(data),
    type: PROMOTION_TYPE.MORE_FOR_LESS,
    conditions: mapConditions(data),
    restrictions: mapMoreForLessRestrictions(data),
    moreForLess: mapMoreForLessEffect(data),
  }
}
